import { useQuery, useMutation } from '@apollo/react-hooks';
import { ApiEvents } from 'analytics';
import { Queries, Mutations } from 'gql';
import _ from 'lodash';
import { LoggerFactory } from 'logger';
import Group from 'model/Group';
import GroupId from 'model/GroupId';
import Location from 'model/Location';
import LocationId from 'model/LocationId';
import MetroId from 'model/MetroId';
import Service from 'model/Service';
import ServiceConnection from 'model/ServiceConnection';
import ServiceConnectionId from 'model/ServiceConnectionId';
import ServiceId from 'model/ServiceId';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import DocumentTitle from 'react-document-title';
import ObjectUtils from 'utils/ObjectUtils';
import Page from 'utils/Page';
import StopWatch from 'utils/StopWatch';
import ProjectDetailViewHeader from 'views/components/Header/ProjectDetailViewHeader';

import AspDataChangeNotification from '../components/AspDataChangeNotification';
import ExportModal from '../components/ExportModal';
import InfoPanel from '../components/InfoPanel';

import MetrosModal from '../components/MetrosModal';
import ProjectMap from '../components/ProjectMap';
import PhasePanel from '../components/PhasePanel';
import DeletePhaseModal from './modals/DeletePhaseModal';
import RenamePhaseModal from './modals/RenamePhaseModal';
import './ProjectDetailView.scss';
import './Sidebar.scss';
import ProjectMetro from 'model/ProjectMetro';
import { useCurrentProject, withProjectProvider } from 'providers/ProjectProvider';
import { dismissAllToasts, toast } from 'utils/Toast';
import useKeyUp from 'hooks/useKeyUp';
import cn from 'classnames';
import { useMarketplaceSegments, withMarketplaceProvider } from 'providers/MarketplaceProvider';
import FeatureEvents from 'analytics/FeatureEvents';
import MapLayersPanel from 'views/components/MapLayersPanel';
import { getMapStyleUrl, setMapStyle } from 'views/components/ProjectMap/helpers/storeMapStyle';
import { defaultCapacityConfiguration, defaultLayerConfiguration, useProjectMapState } from './useProjectMapState';

const ProjectDetailView = ({ user }) => {
  const ref = useRef(null);
  const {
    projectId,
    project,
    isTemplate,
    template,
    phase,
    services,
    changePhase,
    phaseLoading,
    projectLoading,
    error: projectError,
    isEditing,
  } = useCurrentProject();
  const [trackedProjectLoad, setTrackedProjectLoad] = useState(false);
  const { data: metrosData, loading: metrosLoading, error: metrosError } = useQuery(Queries.GET_METROS);
  const { data: layersData } = useQuery(Queries.GET_LAYERS_CONFIGURATION, { variables: { phaseId: phase?.id } });
  const [updateLayersConfiguration] = useMutation(Mutations.UPDATE_LAYERS_CONFIGURATION);
  const { data: onRampsData } = useQuery(Queries.GET_ON_RAMP_CLOUDS);
  const companyId = user?.companyId;
  const { data: companyData, loading: companyLoading } = useQuery(Queries.GET_COMPANY, { variables: { companyId }, skip: !companyId });
  const [execReorderPhases] = useMutation(Mutations.UPDATE_PROJECT_PHASE);

  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [infoView, setInfoView] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isMetrosModalOpen, setIsMetrosModalOpen] = useState(false);
  const [isAspUpdateModalOpen, setIsAspUpdateModalOpen] = useState(true);
  const [openPhasesModals, setOpenPhasesModals] = useState({
    rename: false,
    delete: false,
  });
  const [phaseModalData, setPhaseModalData] = useState({});
  const [metroPanelTab, setMetroPanelTab] = useState(0); //FIXME it's not working
  const [platform, setPlatform] = useState(null);
  const [platformMetros, setPlatformMetros] = useState(null);
  const [product, setProduct] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [zoom, setZoom] = useState();
  const { marketplaceSegments } = useMarketplaceSegments();
  const {
    metros,
    connectionType,
    setConnectionType,
    infraComponentCard,
    setInfraComponentCard,
    ringLatency,
    setRingLatency,
    ringColor,
    setRingColor,
    availableRegion,
    setAvailableRegion,
    metroType,
    setMetroType,
    cloudsList,
    onRamps,
    layers,
    onRampConfiguration,
    setOnRampConfiguration,
    capacityConfiguration,
    setCapacityConfiguration,
    mapUrl,
    setMapUrl,
    selectedMapStyle,
    setSelectedMapStyle,
    selectedOnRamps,
    setSelectedOnRamps,
    openMapMenu,
    setOpenMapMenu,
    mapType,
    setMapType,
    selectedLayer,
    setSelectedLayer,
    capOnRamp,
    setCapOnRamp,
    marketplaceSegment,
    setMarketplaceSegment,
    selectedEms,
    setSelectedEms,
    toggleLayer,
    toggleAvailableRegions,
    toggleLabel,
  } = useProjectMapState({ metrosData, onRampsData, layersData, marketplaceSegments });
  const _logger = LoggerFactory.getLogger('ProjectDetailView');
  const sw = StopWatch.startNew();

  const loading = useMemo(() => projectLoading || metrosLoading || companyLoading || !phase || !project, [
    projectLoading,
    metrosLoading,
    companyLoading,
    project,
    phase,
  ]);
  const error = useMemo(() => !loading && (projectError || metrosError), [projectError, metrosError, loading]);

  //TODO: this should be updated to call _onSelectionChanged once map uses actual data (Metro, Location, Service) objects
  const select = (type, selectedItem) => {
    if (selectedItem instanceof ServiceConnection) {
      return _onSelectionChanged(selectedItem);
    }

    if (selectedItem) {
      const infoView = selectedItem instanceof ProjectMetro ? 'EditMetroView' : 'EditLocationView';
      openInfoView(infoView, selectedItem.id);
    } else {
      setSelectedItemId(null);
      setIsInfoOpen(false);
    }
  };

  const onChangePhase = (phaseId) => {
    try {
      dismissAllToasts();
      setSelectedItemId(null);
      setIsInfoOpen(false);
      changePhase(phaseId);
      _onClose();
    } catch (error) {
      return toast('An unexpected error occurred while changing phases', 'error');
    }
  };

  useEffect(() => {
    if (!ref?.current || !phase) return;
    const phasesHeader = ref.current.querySelector('.phases-header');
    const phaseTab = phasesHeader?.querySelector(`[id='${phase.id.value}']`);
    if (!phaseTab) return;
    phaseTab.scrollIntoView();
  }, [phase, ref]);

  const onReorderPhases = async (phaseId, order) => {
    if (!phaseId) return;
    try {
      await execReorderPhases({ variables: { projectId, phaseId, order } });
      dismissAllToasts();
    } catch (error) {
      return toast('An unexpected error occurred while reordering phases', 'error');
    }
  };

  const changeOpenMapMenu = (menuName) => {
    setOpenMapMenu((prev) => (prev === menuName ? null : menuName));
    if (menuName === 'layers') FeatureEvents.mapLayersShown(projectId, phase.id);
  };

  const _onClose = async () => {
    setIsInfoOpen(false);
    setSelectedItemId(null);
    setInfoView(null);
  };

  useKeyUp('Escape', _onClose);

  const _onError = () => {
    setIsInfoOpen(false);
    setSelectedItemId(null);
    setInfoView(null);
  };

  const openInfoView = (viewName, itemId = null) => {
    setIsInfoOpen(true);
    setSelectedItemId(itemId);
    setInfoView(viewName);
  };

  const _onAddLocations = () => openInfoView('AddLocationsView');

  const _onAddMultipleLocations = () => openInfoView('AddMultipleLocationsView');

  const _onASPImport = () => openInfoView('ASPImportView');

  const _onAddService = () => openInfoView('AddServiceView');

  const _onAddMetro = () => openInfoView('AddMetroView');

  const _onAddEdge = () => {
    openInfoView('AddEdgeView');
    setPlatform('edge');
  };

  const _onExportBriefing = async () => setIsExportModalOpen(true);

  const _onManageMetros = () => setIsMetrosModalOpen(true);

  const _onMetroPanelTabChange = (metroPanelTab) => setMetroPanelTab(metroPanelTab);

  const _onCloseExportBriefing = () => setIsExportModalOpen(false);
  const _onCloseMetrosModal = () => setIsMetrosModalOpen(false);
  const _onCloseAspUpdateModal = () => setIsAspUpdateModalOpen(false);

  const onOpenPhasesModals = (modalName, modalData) => {
    _onClosePhasesModals();
    setOpenPhasesModals((prevState) => ({ ...prevState, [modalName]: true }));
    setPhaseModalData({ ...modalData, projectId });
  };

  const _onClosePhasesModals = () => {
    setOpenPhasesModals({
      rename: false,
      delete: false,
    });
    setPhaseModalData({});
  };

  const phaseModalsProps = {
    onClose: _onClosePhasesModals,
    phaseModalData,
  };

  const _onSelectionChanged = async (selectedItem, keepOpen) => {
    if (!selectedItem) {
      if (keepOpen) {
        setSelectedItemId(null);
        return;
      }
      setSelectedItemId(null);
      setInfoView(null);
      setIsInfoOpen(false);
      return;
    }
    if (selectedItem.id && selectedItem.id.equals(selectedItemId)) {
      return; // do nothing, it's the same item
    }
    _logger.debug(`Selected item changed: ${selectedItem}`);

    //depending on the selected object type, we'll open a certain edit view
    const isMetro = selectedItem instanceof ProjectMetro;
    const isGroup = selectedItem instanceof Group;
    const isLocation = selectedItem instanceof Location;
    const isService = selectedItem instanceof ServiceConnection;
    const isUnconnectedService = selectedItem instanceof Service;
    const isAspSummary = selectedItem === 'aspSummary';
    const isMultipleLocations = selectedItem === 'multipleLocations';

    if (isMetro) {
      openInfoView('EditMetroView', selectedItem.id);
    } else if (isGroup) {
      openInfoView('EditGroupView', selectedItem.id);
    } else if (isLocation) {
      openInfoView('EditLocationView', selectedItem.id);
    } else if (isService) {
      openInfoView('EditServiceView', selectedItem.id);
    } else if (isUnconnectedService) {
      openInfoView('AddServiceView', selectedItem.id);
      setSelectedRegions([]);
    } else if (isAspSummary) {
      openInfoView('ASPInfo', 'aspSummary');
    } else if (isMultipleLocations) {
      openInfoView('AddMultipleLocationsView');
    }
  };

  const _enableRegion = (region) => {
    setSelectedRegions(selectedRegions.concat([region]));
  };

  const _disableRegion = (region) => {
    setSelectedRegions(selectedRegions.filter((r) => r && !r.equals(region)));
  };

  const _onPlatformMetrosChange = (newPlatformMetros, newProduct) => {
    if (!_.isEqual(newPlatformMetros, platformMetros) || !_.isEqual(newProduct, product)) {
      setPlatformMetros(newPlatformMetros);
      setProduct(newProduct);
    }
  };

  const _onChangeZoom = (coordinates) => {
    setZoom(coordinates);
  };

  useEffect(() => {
    return () => dismissAllToasts();
  }, []);

  useEffect(() => {
    if (platform !== null) {
      if (infoView !== 'AddMetalView' && infoView !== 'AddEdgeView') {
        setPlatform(null);
      }
    }
  }, [platform, infoView]);

  useEffect(() => {
    if (trackedProjectLoad || !project) {
      return;
    }

    _logger.debug(`Project loaded successfully in ${sw.milliseconds}ms`);

    //track project load timing
    ApiEvents.projectLoaded(sw.milliseconds);

    sw.stop();
    setTrackedProjectLoad(true);
    // eslint-disable-next-line
  }, [project]);

  const getSelectedItem = (selectedItemId, data) => {
    const { phase } = data;
    const isMetro = selectedItemId instanceof MetroId;
    const isGroup = selectedItemId instanceof GroupId;
    const isLocation = selectedItemId instanceof LocationId;
    const isService = selectedItemId instanceof ServiceConnectionId;
    const isUnconnectedService = selectedItemId instanceof ServiceId;

    if (isMetro) {
      return phase.metros.find((item) => ObjectUtils.equals(selectedItemId, item.id));
    } else if (isGroup) {
      return phase.allGroups.find((item) => ObjectUtils.equals(selectedItemId, item.id));
    } else if (isLocation) {
      return phase.allLocations.find((item) => ObjectUtils.equals(selectedItemId, item.id));
    } else if (isService) {
      const services = phase.allServices;
      return services.find((item) => ObjectUtils.equals(selectedItemId, item.id));
    } else if (isUnconnectedService) {
      return phase.services.find((item) => ObjectUtils.equals(selectedItemId, item.id));
    }
  };

  const collapseSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const changeMapStyle = (mapStyle) => {
    setMapStyle(mapStyle);
    setSelectedMapStyle(mapStyle);
    setMapUrl(getMapStyleUrl(mapStyle));
    // this.setState({ selectedMapStyle: mapStyle, mapUrl: getMapStyleUrl(mapStyle) });
  };

  const _updateLayersConfiguration = async (newValue) => {
    await updateLayersConfiguration({
      variables: { params: { phaseId: phase.id, ...newValue } },
      refetchQueries: [{ query: Queries.GET_LAYERS_CONFIGURATION, variables: { phaseId: phase.id } }],
      awaitRefetchQueries: true,
    });
  };

  const changeMarketplaceSegment = (segment) => {
    setMarketplaceSegment(segment);
    _updateLayersConfiguration({ marketplaceSegment: segment?.id || '' });
  };

  if (error) {
    return (
      <DocumentTitle title={Page.title('Error! | Projects')}>
        <p style={{ margin: '100px' }}>ERROR! {error.message}</p>
      </DocumentTitle>
    );
  }

  if (loading) {
    return (
      <DocumentTitle title={Page.title('(Loading) | Projects')}>
        <div className="loading-container">
          <span className="loading-text">
            <div className="spinner"></div>
            Loading {isTemplate ? 'Template' : 'Project'}
          </span>
        </div>
      </DocumentTitle>
    );
  }

  const selectedItem = getSelectedItem(selectedItemId, { phase, metros, services });
  const isAspInfoOpen = isInfoOpen && selectedItemId === 'aspSummary';
  const isDSPAllowed = companyData?.company?.isDSPAllowed ?? false;
  const isCapacityAllowed = companyData?.company?.isCapacityAllowed ?? false;

  return (
    <DocumentTitle title={Page.title(`${project.name} | Projects`)}>
      <DndProvider backend={Backend}>
        <div className={`app ${isInfoOpen ? 'info-panel-open' : ''}`} ref={ref}>
          {isExportModalOpen && (
            <ExportModal
              project={project}
              phase={phase}
              metros={metros}
              onClose={_onCloseExportBriefing}
              connectionType={connectionType}
              layers={layers}
              ringLatency={ringLatency}
              ringColor={ringColor}
              metroType={metroType}
              mapType={mapType}
              marketplaceSegment={marketplaceSegment}
              selectedOnRamps={selectedOnRamps}
              onRampConfiguration={onRampConfiguration}
              capacityConfiguration={capacityConfiguration}
              capOnRamp={capOnRamp}
              selectedEms={selectedEms}
            />
          )}

          <DeletePhaseModal isOpen={openPhasesModals.delete} onChangePhase={onChangePhase} {...phaseModalsProps} />
          <RenamePhaseModal isOpen={openPhasesModals.rename} {...phaseModalsProps} />

          {isMetrosModalOpen && <MetrosModal projectId={projectId} phase={phase} metros={metros} onClose={_onCloseMetrosModal} />}

          {isAspUpdateModalOpen && <AspDataChangeNotification projectId={projectId} phase={phase} onClose={_onCloseAspUpdateModal} />}

          <ProjectDetailViewHeader user={user} onChangePhase={onChangePhase} onReorderPhases={onReorderPhases} onOpenPhasesModals={onOpenPhasesModals} />

          <div className={cn('sidebar', { isSidebarCollapsed })}>
            <PhasePanel
              phase={phase}
              projectId={projectId}
              selectedItem={selectedItem}
              onAddLocations={_onAddLocations}
              onAddMultipleLocations={_onAddMultipleLocations}
              onASPImport={_onASPImport}
              onAddService={_onAddService}
              onAddMetro={_onAddMetro}
              onAddEdge={_onAddEdge}
              onExportBriefing={_onExportBriefing}
              onSelectionChanged={_onSelectionChanged}
              onClose={_onClose}
              onError={_onError}
              isAspInfoOpen={isAspInfoOpen}
              loadingPhase={phaseLoading}
              collapseSidebar={collapseSidebar}
              isSidebarCollapsed={isSidebarCollapsed}
              template={template}
              mapConfig={{
                connectionType,
                layers,
                ringLatency,
                ringColor,
                metroType,
                mapType,
                marketplaceSegment,
              }}
              companyId={companyId}
              isDSPAllowed={isDSPAllowed}
            />

            <InfoPanel
              phaseId={phase.id}
              servicesConnections={phase.unconnectedServices}
              projectId={projectId}
              phase={phase}
              selectedItem={selectedItem}
              selectedRegions={selectedRegions}
              activeMetros={phase.metros}
              directConnections={phase.directConnections}
              metros={metros}
              services={services}
              view={infoView}
              onClose={_onClose}
              isInfoOpen={isInfoOpen}
              onMetroPanelTabChange={_onMetroPanelTabChange}
              metroPanelTab={metroPanelTab}
              onError={_onError}
              onSelectionChanged={_onSelectionChanged}
              onEnableRegion={_enableRegion}
              onDisableRegion={_disableRegion}
              onPlatformMetrosChange={_onPlatformMetrosChange}
              onChangeZoom={_onChangeZoom}
              capacityConfiguration={capacityConfiguration}
              isCapacityAllowed={isCapacityAllowed}
            />
          </div>

          <div className="right-sidebar">
            {selectedLayer && (
              <MapLayersPanel
                selectedLayer={selectedLayer}
                setSelectedLayer={setSelectedLayer}
                capacityConfiguration={capacityConfiguration}
                setCapacityConfiguration={setCapacityConfiguration}
                onRampConfiguration={onRampConfiguration}
                selectedOnRamps={selectedOnRamps}
                setOnRampConfiguration={setOnRampConfiguration}
                setSelectedOnRamps={setSelectedOnRamps}
                onRampsOptions={cloudsList}
                mapType={mapType}
                marketplaceSegment={marketplaceSegment}
                onChangeMapType={setMapType}
                onChangeMarketplaceSegment={changeMarketplaceSegment}
                connectionType={connectionType}
                setConnectionType={setConnectionType}
                infraComponentCard={infraComponentCard}
                setInfraComponentCard={setInfraComponentCard}
                layers={layers}
                toggleLayer={toggleLayer}
                toggleAvailableRegions={toggleAvailableRegions}
                ringLatency={ringLatency}
                setRingLatency={setRingLatency}
                ringColor={ringColor}
                setRingColor={setRingColor}
                availableRegion={availableRegion}
                setAvailableRegion={setAvailableRegion}
                metroType={metroType}
                setMetroType={setMetroType}
                changeOpenMapMenu={changeOpenMapMenu}
                changeMapStyle={changeMapStyle}
                selectedMapStyle={selectedMapStyle}
                toggleLabel={toggleLabel}
                capOnRamp={capOnRamp}
                setCapOnRamp={setCapOnRamp}
                selectedEms={selectedEms}
                setSelectedEms={setSelectedEms}
                updateLayersConfiguration={_updateLayersConfiguration}
              />
            )}
          </div>
          <ProjectMap
            user={user}
            phaseId={phase.id}
            projectId={projectId}
            project={project}
            phase={phase}
            metros={metros}
            isInfoOpen={isInfoOpen}
            onSelect={select}
            onEnableRegion={_enableRegion}
            onDisableRegion={_disableRegion}
            onManageMetros={_onManageMetros}
            selected={selectedItem}
            selectedRegions={selectedRegions}
            connectionType={connectionType}
            setConnectionType={setConnectionType}
            setInfraComponentCard={setInfraComponentCard}
            infraComponentCard={infraComponentCard}
            layers={layers}
            toggleLayer={toggleLayer}
            ringLatency={ringLatency}
            setRingLatency={setRingLatency}
            ringColor={ringColor}
            setRingColor={setRingColor}
            availableRegion={availableRegion}
            onMetroPanelTabChange={_onMetroPanelTabChange}
            setAvailableRegion={setAvailableRegion}
            metroType={metroType}
            setMetroType={setMetroType}
            toggleAvailableRegions={toggleAvailableRegions}
            platform={platform}
            platformMetros={platformMetros}
            product={product}
            onClose={_onClose}
            isSidebarCollapsed={isSidebarCollapsed}
            openMapMenu={openMapMenu}
            changeOpenMapMenu={changeOpenMapMenu}
            mapType={mapType}
            capOnRamp={capOnRamp}
            setCapOnRamp={setCapOnRamp}
            marketplaceSegment={marketplaceSegment}
            onChangeMapType={setMapType}
            onChangeMarketplaceSegment={changeMarketplaceSegment}
            isEditing={isEditing}
            zoom={zoom}
            toggleLabel={toggleLabel}
            cloudsList={cloudsList}
            onRamps={onRamps}
            onRampConfiguration={onRampConfiguration}
            capacityConfiguration={capacityConfiguration}
            selectedOnRamps={selectedOnRamps}
            setOnRampConfiguration={setOnRampConfiguration}
            setCapacityConfiguration={setCapacityConfiguration}
            setSelectedOnRamps={setSelectedOnRamps}
            isCapacityAllowed={isCapacityAllowed}
            selectedLayer={selectedLayer}
            setSelectedLayer={setSelectedLayer}
            mapUrl={mapUrl}
            setMapUrl={setMapUrl}
            selectedMapStyle={selectedMapStyle}
            setSelectedMapStyle={setSelectedMapStyle}
            defaultLayerConfiguration={defaultLayerConfiguration}
            defaultCapacityConfiguration={defaultCapacityConfiguration}
            selectedEms={selectedEms}
            setSelectedEms={setSelectedEms}
          />
        </div>
      </DndProvider>
    </DocumentTitle>
  );
};

export default withProjectProvider(withMarketplaceProvider(ProjectDetailView));
